/**
 * How a recipient name becomes a link to an organization: the three tiers,
 * the sponsor rule, what was measured and on what, and what a reader should
 * still check. Written for the researcher who asks about precision before
 * anything else, and for the journalist who needs to know what "resolved"
 * on a grant row is a claim about.
 */
import type { DocPage } from '../types.js';

export const resolutionPage: DocPage = {
  slug: 'resolution',
  title: 'How a name becomes an organization',
  audience: 'researchers, journalists',
  promise: 'The three tiers that link a recipient name to a filer, the sponsor rule, the measured precision and its sample, and what a resolved link does and does not claim.',
  lead: 'Most private foundations write only a name and a city on their grant lists. FunderGraph links those names to organizations in three tiers of decreasing certainty, measures the result on names that other filers wrote with a number, and shows every link with the tier that made it.',
  sections: [
    {
      id: 'problem',
      heading: 'The problem',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A Form 990 Schedule I row usually states the recipient’s EIN; a Form 990-PF Part XV row almost never does. In the national Silver of 12 October 2026, 2,914,224 grant rows named a recipient without a number, 1,121,568 distinct names once case is folded. A grant to a name is a fact about a name; a grant to a page is a fact about an organization. The resolver is what turns the first into the second, and it would rather leave a name unlinked than link it wrongly, because a wrong link puts a grant on the wrong organization’s page under a citation that looks as sound as every other.',
          ],
        },
      ],
    },
    {
      id: 'tiers',
      heading: 'The three tiers',
      blocks: [
        {
          kind: 'list',
          items: [
            ['Exact. The name, folded for case, accents, punctuation, and spacing, matches exactly one organization in the master file in the same state. When two organizations share the name, the city the filer wrote decides; without a city, neither is chosen.'],
            ['Rule. The core of the name, with legal forms and articles removed and common abbreviations spelled out, matches exactly one organization in the state. "A New Leaf Inc" and "New Leaf" meet here.'],
            ['Nearest name. The organization whose name is nearest by a text embedding clears a similarity threshold, leads the runner-up by a margin, and either agrees on the city or shares a word that is rare in that state. This tier exists for the names no other filer ever wrote with a number: the misspellings and the variants.'],
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'Three rules sit over every tier. A city the filer stated rules out a candidate in another city, because the filer said something about the recipient and a candidate elsewhere contradicts it. A government body is never linked, because the master file does not hold governments. A recipient in a state the master file does not cover is never forced onto the nearest name in a state it is not in. Every name the tiers refuse goes to a review queue with its reason and its best candidates, where it waits rather than guesses.',
          ],
        },
      ],
    },
    {
      id: 'sponsors',
      heading: 'Sponsored projects',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A fiscal sponsor files one return for many projects, and a grant to a project appears on a funder’s list under the project’s name. FunderGraph treats the sponsor as the identity authority: a project named in a sponsor’s own program text, or in a grant that states the sponsor’s number beside the project’s name, is recorded as that sponsor’s project, with an identifier scoped under the sponsor’s EIN. Grants to it are shown on the sponsor’s page in the project’s context, and the project has no page of its own until it claims a listing. ',
            { link: '/about/sponsors', text: 'The page for sponsors' },
            ' says how that looks and how to correct it.',
          ],
        },
      ],
    },
    {
      id: 'measured',
      heading: 'What was measured',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Precision was measured on the fixture, a pinned snapshot of real filings from one state, against names two filers wrote: one stated the recipient’s number on Schedule I, the other wrote only the name on Part XV. Those pairs are the labeled sample, 942 names in September 2026. The resolver linked them at a precision of 0.9936; the misses include one group that files under two numbers for one name, where three filers name the parent and one states the affiliate’s number. The floor the build promises, 0.95, is asserted of each tier the labels reach rather than of the total, so that the tier most likely to be wrong cannot hide behind the two least likely.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'The labels barely reach the nearest-name tier, by construction: a label exists only where two filers spelled a name the same way, and those names the first two tiers already join. That tier was measured on its own by sweeping its threshold and margin over the sample, and its threshold was set where the labels stop seeing a difference. Its precision on names the labels cannot see is not known, and the badge on a grant row says which tier made the link so a reader can weigh it.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'Coverage is a different number from precision. On the Arizona snapshot, of 23,716 names, 11,903 named a recipient in another state, which a one-state build cannot resolve by construction and a national one can; of the 11,813 in-state names, 42 percent resolved through the first two tiers and 5,393 had no candidate at all, which is the work the nearest-name tier exists for. In the current national build the nearest-name tier is inactive, because the embeddings it needs wait on a batch job the account cannot yet run; nationally, only the first two tiers link names today. ',
            { link: '/about/coverage#not', text: 'What is not in the graph' },
            ' lists that with the other gaps.',
          ],
        },
      ],
    },
    {
      id: 'reading',
      heading: 'What a resolved link claims',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A grant row on an organization’s page that carries the badge "resolved" says: the funder wrote a name, not a number, and FunderGraph matched that name to this organization by the tier the badge names. The grant itself, its amount, its purpose, and its period, is the funder’s statement as filed. A grant row without the badge either stated this organization’s number, or is shown under the name the funder wrote because no tier accepted it; a name without a link counts toward nothing but that funder’s own list.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'To check a link, open the funder’s return at the link beside the grant and find the row; the name there is what the resolver read. If the link is wrong, write to the address on ',
            { link: '/about#contact', text: 'the about page' },
            ' with the page, the grant, and the organization the funder meant, and the name goes to the review queue with your note.',
          ],
        },
      ],
    },
  ],
};
